/**
 * Modal helpers for Trade Finance Case Study with Modal.
 */
import { __ } from '@wordpress/i18n';
import { MODAL_ICONS, MODAL_ICONS_BY_VARIANT } from './modal-constants';
import { decodeHtmlEntities } from '../shared/html-entities';

export { decodeHtmlEntities };
export { getModalChromeLabel } from './modal-i18n';

const VARIANT_FUND = 'fund';
const VARIANT_CAPITAL = 'capital';
const COLOR_GREEN = '#6dbe45';
const COLOR_BLUE = '#009bab';

/**
 * @param {string} [prefix] ID prefix.
 * @return {string} Unique id.
 */
function createLocalId( prefix = 'qa' ) {
	const random = Math.random().toString( 36 ).slice( 2, 9 );
	return `${ prefix }-${ Date.now().toString( 36 ) }-${ random }`;
}

/**
 * @param {string} blockInstanceId Block instance id.
 * @return {string} Safe slug fragment.
 */
export function getSafeInstanceId( blockInstanceId ) {
	return String( blockInstanceId || 'default' ).replace(
		/[^a-zA-Z0-9_-]/g,
		'-'
	);
}

/**
 * @param {string} blockInstanceId Block instance id.
 * @return {string} Modal root id.
 */
export function getModalRootId( blockInstanceId ) {
	return `alteia-tfcs-modal-${ getSafeInstanceId( blockInstanceId ) }-root`;
}

/**
 * @param {string} blockInstanceId Block instance id.
 * @param {string} suffix          Element suffix.
 * @return {string} Element id.
 */
export function getModalElementId( blockInstanceId, suffix ) {
	return `alteia-tfcs-modal-${ getSafeInstanceId(
		blockInstanceId
	) }-${ suffix }`;
}

/**
 * Create a single Q&A item.
 *
 * @param {Object} overrides Optional overrides.
 * @return {Object} Q&A item.
 */
export function createQuestionItem( overrides = {} ) {
	return {
		id: createLocalId( 'qa' ),
		question: '',
		answer: '',
		...overrides,
	};
}

/**
 * Default Q&A items matching the design screenshots.
 *
 * @return {Array} Question items.
 */
export function createDefaultQuestions() {
	return [
		createQuestionItem( {
			id: 'qa-opportunity',
			question: __( 'The opportunity', 'alteia' ),
			answer: __(
				'The borrower is an established agricultural exporter operating across multiple West African origins, sourcing raw cashew nuts from smallholder farmer cooperatives and exporting to approved buyers across India, Vietnam, and the UAE. The business required working capital to fund procurement from farmers and cooperatives ahead of consolidation and shipment, bridging the period between procurement and export proceeds.',
				'alteia'
			),
		} ),
		createQuestionItem( {
			id: 'qa-structured',
			question: __( 'How Alteia structured the facility', 'alteia' ),
			answer: __(
				'Alteia provided pre-export financing against identified inventory and approved offtake arrangements with established international buyers. Capital was deployed only after the underlying trade flow, security package, and repayment pathway had been confirmed. Security included cession of export receivables and inventory control during the consolidation period. The facility tenor was aligned to the cashew export cycle.',
				'alteia'
			),
		} ),
		createQuestionItem( {
			id: 'qa-repayment',
			question: __( 'Repayment', 'alteia' ),
			answer: __(
				'Repayment was sourced from export proceeds upon delivery and receipt of payment from approved international buyers under agreed commercial terms.',
				'alteia'
			),
		} ),
	];
}

/**
 * Create default modal content for a new card.
 *
 * @param {Object} overrides Optional overrides.
 * @return {Object} Modal content object.
 */
export function createDefaultModalContent( overrides = {} ) {
	const { questions: questionsOverride, ...rest } = overrides || {};
	const questions = Array.isArray( questionsOverride )
		? questionsOverride
		: createDefaultQuestions();

	return {
		region: __( 'West Africa', 'alteia' ),
		facilityType: __( 'Pre-Export Finance', 'alteia' ),
		typicalTenor: __( '90 Days', 'alteia' ),
		commodity: __( 'Raw cashew nuts', 'alteia' ),
		regionIconId: 0,
		regionIconUrl: '',
		facilityTypeIconId: 0,
		facilityTypeIconUrl: '',
		typicalTenorIconId: 0,
		typicalTenorIconUrl: '',
		commodityIconId: 0,
		commodityIconUrl: '',
		...rest,
		questions,
	};
}

/**
 * Normalize modal content from saved / legacy shapes.
 *
 * @param {Object} raw Raw modal content.
 * @return {Object} Normalized modal content.
 */
export function normalizeModalContent( raw = {} ) {
	const questions = Array.isArray( raw.questions )
		? raw.questions.map( ( item, index ) =>
				createQuestionItem( {
					id: item?.id || `qa-${ index + 1 }`,
					question: item?.question || '',
					answer: item?.answer || '',
				} )
		  )
		: createDefaultQuestions();

	return {
		region: raw.region || '',
		facilityType: raw.facilityType || '',
		typicalTenor: raw.typicalTenor || '',
		commodity: raw.commodity || '',
		regionIconId: Number( raw.regionIconId ) || 0,
		regionIconUrl: raw.regionIconUrl || '',
		facilityTypeIconId: Number( raw.facilityTypeIconId ) || 0,
		facilityTypeIconUrl: raw.facilityTypeIconUrl || '',
		typicalTenorIconId: Number( raw.typicalTenorIconId ) || 0,
		typicalTenorIconUrl: raw.typicalTenorIconUrl || '',
		commodityIconId: Number( raw.commodityIconId ) || 0,
		commodityIconUrl: raw.commodityIconUrl || '',
		questions: questions.length > 0 ? questions : createDefaultQuestions(),
	};
}

/**
 * Resolve default icon URLs for a website variant.
 *
 * Custom uploads are applied separately via resolveModalMetaIcons().
 *
 * @param {string} [websiteVariant] fund|capital
 * @return {Object} Icon URL map.
 */
export function getModalIconUrls( websiteVariant ) {
	const variant = resolveVariant( websiteVariant );
	const config =
		typeof window !== 'undefined' &&
		window.alteia_gutenberg_blocks_config &&
		window.alteia_gutenberg_blocks_config.trade_finance_case_study_modal
			? window.alteia_gutenberg_blocks_config
					.trade_finance_case_study_modal
			: {};
	const fallbacks = MODAL_ICONS_BY_VARIANT[ variant ] || MODAL_ICONS;
	const variantConfig =
		config.defaultIcons && config.defaultIcons[ variant ]
			? config.defaultIcons[ variant ]
			: {};

	return {
		region: variantConfig.region || fallbacks.region,
		facilityType: variantConfig.facilityType || fallbacks.facilityType,
		typicalTenor: variantConfig.typicalTenor || fallbacks.typicalTenor,
		commodity: variantConfig.commodity || fallbacks.commodity,
		close: config.closeIconUrl || MODAL_ICONS.close,
	};
}

/**
 * Resolve sidebar meta icons: custom upload per field, else variant default.
 *
 * @param {Object} modalContent   Normalized modal content.
 * @param {string} websiteVariant Section variant.
 * @return {Object} Icon URL map for the four sidebar fields.
 */
export function resolveModalMetaIcons( modalContent = {}, websiteVariant ) {
	const defaults = getModalIconUrls( websiteVariant );

	return {
		region: modalContent.regionIconUrl || defaults.region,
		facilityType: modalContent.facilityTypeIconUrl || defaults.facilityType,
		typicalTenor: modalContent.typicalTenorIconUrl || defaults.typicalTenor,
		commodity: modalContent.commodityIconUrl || defaults.commodity,
		close: defaults.close,
	};
}

/**
 * @param {string} websiteVariant Raw variant.
 * @return {string} fund|capital
 */
function resolveVariant( websiteVariant ) {
	return websiteVariant === VARIANT_CAPITAL ? VARIANT_CAPITAL : VARIANT_FUND;
}

/**
 * Resolve modal accent, falling back to section variant color.
 *
 * @param {Object} card           Card attributes.
 * @param {string} websiteVariant Section variant.
 * @return {string} Accent color.
 */
export function resolveModalAccentColor( card, websiteVariant ) {
	if ( card?.modalAccentColor ) {
		return card.modalAccentColor;
	}

	if ( card?.accentColor ) {
		return card.accentColor;
	}

	return resolveVariant( websiteVariant ) === VARIANT_CAPITAL
		? COLOR_BLUE
		: COLOR_GREEN;
}

/**
 * Build frontend JSON payload for the modal system.
 *
 * @param {Object}  attributes       Block attributes.
 * @param {Object}  [options]        Options.
 * @param {boolean} [options.legacy] Omit newer flags for deprecated saves.
 * @return {Object} Modal section data.
 */
export function buildModalSectionData( attributes, { legacy = false } = {} ) {
	const { blockInstanceId, websiteVariant, cards = [] } = attributes;
	const resolvedVariant = resolveVariant( websiteVariant );
	const icons = getModalIconUrls( resolvedVariant );

	return {
		blockInstanceId: blockInstanceId || '',
		websiteVariant: resolvedVariant,
		buttonHoverColor:
			resolvedVariant === VARIANT_CAPITAL ? COLOR_BLUE : COLOR_GREEN,
		icons,
		cards: cards.map( ( card ) => {
			const modalContent = normalizeModalContent( card.modalContent );
			const data = {
				id: card.id || '',
				modalEnabled: card.modalEnabled !== false,
				modalTitle: card.modalTitle || card.title || '',
				badgeLabel: card.badgeLabel || '',
				badgeEnabled: card.badgeEnabled !== false,
				imageUrl: card.imageUrl || '',
				imageAlt: card.imageAlt || '',
				modalAccentColor: resolveModalAccentColor(
					card,
					websiteVariant
				),
				modalContent,
				icons: resolveModalMetaIcons( modalContent, resolvedVariant ),
			};

			if ( ! legacy && card.modalTitleEnabled === false ) {
				data.modalTitleEnabled = false;
			}

			return data;
		} ),
	};
}

/**
 * Find card modal data by id.
 *
 * @param {Object} sectionData Section modal payload.
 * @param {string} cardId      Card id.
 * @return {Object|null} Card modal data.
 */
export function findCardModalData( sectionData, cardId ) {
	if ( ! sectionData?.cards || ! cardId ) {
		return null;
	}

	return sectionData.cards.find( ( card ) => card.id === cardId ) || null;
}
